// src/pages/Professions/CreatePrompts.js
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useParams} from 'react-router-dom';

import axios from "axios";
import CreatableSelect from "react-select/creatable";
import Tooltip from "@mui/material/Tooltip";
import backendURL from "../../config";


const Loader = () => (
    <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
        borderRadius: 'inherit' // Inherit border radius from parent card
    }}>
        <div style={{
            border: '5px solid #f3f3f3', /* Light grey */
            borderTop: '5px solid #0d6efd', /* Bootstrap Primary Blue */
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `}</style>
    </div>
);


// Shared options for reusability
const commonCommunicationStyles = [
    { value: 'empathetic', label: 'Empathetic' },
    { value: 'informative', label: 'Informative' },
    { value: 'reassuring', label: 'Reassuring' },
    { value: 'professional', label: 'Professional' },
    { value: 'objective', label: 'Objective' },
    { value: 'formal', label: 'Formal' },
    { value: 'encouraging', label: 'Encouraging' },
    { value: 'patient', label: 'Patient' },
    { value: 'enthusiastic', label: 'Enthusiastic' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'concise', label: 'Concise' },
    { value: 'detailed', label: 'Detailed' },
];

const dynamicFieldConfigurations = {
    "Medical Professionals": {
        _default: [
            { id: 'userName', label: "Doctor's Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., Dr. Emily Carter", type: 'text', templateLabel: "**Name:**" },
            { id: 'yearsOfExperience', label: "Years of Experience", placeholder: "e.g., 10 years", type: 'text', templateLabel: "**Years of Experience:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles,
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'patientCenteredCare', label: 'Patient-centered care' },
                    { value: 'evidenceBasedPractice', label: 'Evidence-based practice' },
                    { value: 'confidentiality', label: 'Confidentiality' },
                    { value: 'holisticApproach', label: 'Holistic Approach' },
                ],
                placeholder: "Add new key value"
            },
        ]
    },
    "Financial Professionals": {
        "Tax Accountant": [
            { id: 'userName', label: "Accountant's Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., Sarah Chen, CPA", type: 'text', templateLabel: "**Name:**" },
            { id: 'yearsOfExperience', label: "Years of Experience", placeholder: "e.g., 5 years", type: 'text', templateLabel: "**Years of Experience:**" },
            { id: 'areasOfExpertise', label: "Areas of Expertise", placeholder: "Individual Tax Returns, Small Business Tax...", type: 'textarea', templateLabel: "**Areas of Expertise:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles.filter(s => ['professional', 'objective', 'informative', 'concise', 'detailed'].includes(s.value)),
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'accuracy', label: 'Accuracy' },
                    { value: 'integrity', label: 'Integrity' },
                    { value: 'compliance', label: 'Compliance' },
                    { value: 'timeliness', label: 'Timeliness' },
                ],
                placeholder: "Add new key value"
            },
        ],
        _default: [ // Default for other Finance specialities
            { id: 'userName', label: "Professional's Name", placeholder: "e.g., John Doe", type: 'text', templateLabel: "**Name:**" },
            { id: 'yearsOfExperience', label: "Years of Experience", placeholder: "e.g., 7 years", type: 'text', templateLabel: "**Years of Experience:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles,
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'clientFirst', label: 'Client First' },
                    { value: 'transparency', label: 'Transparency' },
                    { value: 'ethicalPractices', label: 'Ethical Practices' },
                ],
                placeholder: "Add new key value"
            },
        ]
    },
    "Legal Professionals": {
        _default: [
            { id: 'userName', label: "Lawyer's Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., James O'Connell, Esq.", type: 'text', templateLabel: "**Name:**" },
            { id: 'areasOfExpertise', label: "Area of Expertise", placeholder: "Contract Law, Real Estate Law...", type: 'textarea', templateLabel: "**Area of Expertise:**" },
            { id: 'yearsOfExperience', label: "Years of Experience", placeholder: "e.g., 3 years", type: 'text', templateLabel: "**Years of Experience:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles.filter(s => ['formal', 'objective', 'informative', 'assertive', 'persuasive'].includes(s.value) || !commonCommunicationStyles.find(cs => cs.value === s.value)), // Add some specific or use general
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'upholdingTheLaw', label: 'Upholding the law' },
                    { value: 'clientConfidentiality', label: 'Client confidentiality' },
                    { value: 'justice', label: 'Justice' },
                    { value: 'dueDiligence', label: 'Due Diligence' },
                ],
                placeholder: "Add new key value"
            },
        ]
    },
    "Educational Professionals": {
        _default: [
            { id: 'userName', label: "Teacher's Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., Ms. Johnson", type: 'text', templateLabel: "**Name:**" },
            { id: 'gradeLevel', label: "Grade Level", placeholder: "e.g., Elementary School", type: 'text', templateLabel: "**Grade Level:**" },
            { id: 'subjectArea', label: "Subject Area", placeholder: "e.g., Math, Science", type: 'text', templateLabel: "**Subject Area:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles.filter(s => ['encouraging', 'patient', 'informative', 'clear', 'engaging'].includes(s.value) || !commonCommunicationStyles.find(cs => cs.value === s.value)),
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'studentSuccess', label: 'Student success' },
                    { value: 'fosteringCuriosity', label: 'Fostering curiosity' },
                    { value: 'inclusivity', label: 'Inclusivity' },
                    { value: 'lifelongLearning', label: 'Lifelong Learning' },
                ],
                placeholder: "Add new key value"
            },
        ]
    },
    "Other Professionals": {
        "Real Estate Agent": [
            { id: 'userName', label: "Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., Maria Rodriguez", type: 'text', templateLabel: "**Name:**" },
            { id: 'yearsOfExperience', label: "Years of Experience", placeholder: "e.g., 2 years", type: 'text', templateLabel: "**Years of Experience:**" },
            { id: 'areasOfExpertise', label: "Area of Expertise", placeholder: "Residential Sales, Commercial Real Estate...", type: 'textarea', templateLabel: "**Area of Expertise:**" },
            {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles.filter(s => ['enthusiastic', 'friendly', 'informative', 'persuasive', 'negotiating'].includes(s.value) || !commonCommunicationStyles.find(cs => cs.value === s.value)),
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'clientSatisfaction', label: 'Client satisfaction' },
                    { value: 'transparency', label: 'Transparency' },
                    { value: 'marketKnowledge', label: 'Market Knowledge' },
                    { value: 'negotiationSkills', label: 'Negotiation Skills' },
                ],
                placeholder: "Add new key value"
            },
        ],
        _default: [ // Default for "Others" if speciality doesn't match
             { id: 'userName', label: "Name (Pick a name on how the chatbot should be referred)", placeholder: "e.g., Alex Smith", type: 'text', templateLabel: "**Name:**" },
             {
                id: 'communicationStyle',
                label: "Communication Style",
                type: 'dropdown',
                templateLabel: "**Communication Style:**",
                options: commonCommunicationStyles,
            },
            {
                id: 'keyPrinciples',
                label: "Key Values",
                type: 'checkboxGroup',
                templateLabel: "**Key Principles:**",
                defaultOptions: [
                    { value: 'customerService', label: 'Customer Service' },
                    { value: 'quality', label: 'Quality' },
                    { value: 'innovation', label: 'Innovation' },
                ],
                placeholder: "Add new key value"
            },
        ]
    },
    "Faith Related Professionals": {
        "Islamic Scholar": []
    }
};



const CreatePrompts = forwardRef(({ chatbotId: propChatbotId = null,  onFormValidityChange, onComplete, OnError }, ref) => {
    const [professions, setProfessions] = useState([]);
    const [professionOptions, setProfessionOptions] = useState([]);
    const [selectedProfession, setSelectedProfession] = useState(null);
    const [specialityOptions, setSpecialityOptions] = useState([]);
    const [selectedSpeciality, setSelectedSpeciality] = useState(null);
    const [subSkillOptions, setSubSkillOptions] = useState([]);
    const [selectedSubSkills, setSelectedSubSkills] = useState([]);
    const [newSubSkill, setNewSubSkill] = useState("");
    const [error, setError] = useState(null);
    const [assistantType, setAssistantType] = useState('Business Assistance');


    const [currentDynamicFields, setCurrentDynamicFields] = useState([]);
    const [dynamicFieldValues, setDynamicFieldValues] = useState({});

    // State for "Key Values" (field.id === 'keyPrinciples')
    const [availableKeyValueOptions, setAvailableKeyValueOptions] = useState([]);
    const [newKeyValueInput, setNewKeyValueInput] = useState("");

    const [isLoading, setIsLoading] = useState(false); // --- ADDED: Loading state

    const { chatbotId: paramsChatbotId } = useParams();

    useEffect(() => {
        document.title = "Add Profession | KlonIT Web Application";
        fetchProfessions();
    }, []);

    const fetchProfessions = async () => {
        setIsLoading(true)
        try {
            const token = localStorage.getItem("access_token");
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${backendURL}/get-professions`, { headers });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

            if (responseData && responseData.length > 0) {
                setProfessions(responseData);
                const uniqueProfessions = [...new Map(responseData.map(item => [item.profession_name, item])).values()];
                setProfessionOptions(uniqueProfessions.map(item => ({ value: item.profession_name, label: item.profession_name })));
            } else {
                setError("Unexpected response structure or no professions found.");
            }
        } catch (err) {
            console.error("Error fetching professions:", err);
            setError("Error fetching professions.");
        } finally {
            setIsLoading(false)
        }
    };

    const updateUserPromptToLocalStorage = (data) => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const userData = JSON.parse(userStr);
            userData.llm_settings = data || null;
            localStorage.setItem('user', JSON.stringify(userData));
            window.dispatchEvent(new CustomEvent('userLocalStorageUpdated', { detail: userData }));
            console.log("Local storage user subscription updated.");
        }
    };

    const handleProfessionChange = (selectedOption) => {
        setSelectedProfession(selectedOption);
        setSelectedSpeciality(null);
        setSelectedSubSkills([]);
        setCurrentDynamicFields([]);
        setDynamicFieldValues({});
        setAvailableKeyValueOptions([]);
        setNewKeyValueInput("");

        if (selectedOption && !selectedOption.__isNew__) {
            const filteredSpecialities = professions.filter(item => item.profession_name === selectedOption.value);
            const uniqueSpecialities = [...new Map(filteredSpecialities.map(item => [item.speciality, item])).values()];
            setSpecialityOptions(uniqueSpecialities.map(item => ({ value: item.speciality, label: item.speciality })));
        } else {
            // If it's a new profession, there are no existing specialities
            setSpecialityOptions([]);
        }
        onFormValidityChange(false);
    };

    const handleSpecialityChange = (selectedOption) => {
        setSelectedSpeciality(selectedOption);
        setSelectedSubSkills([]);

        let config = [];
        if (selectedProfession && selectedOption) {
            const professionName = selectedProfession.value;
            const specialityName = selectedOption.value;

            // Use default configs if profession/speciality is new
            if (dynamicFieldConfigurations[professionName]) {
                config = dynamicFieldConfigurations[professionName][specialityName] ||
                    dynamicFieldConfigurations[professionName]._default ||
                    [];
            } else if (selectedProfession && selectedProfession.__isNew__) {
                // For a brand new profession, maybe use a generic default if available
                config = dynamicFieldConfigurations["Other Professionals"]?._default || [];
            }
            setCurrentDynamicFields(config);


            const initialValues = {};
            const keyPrinciplesConfig = config.find(f => f.id === 'keyPrinciples' && f.type === 'checkboxGroup');

            if (keyPrinciplesConfig) {
                setAvailableKeyValueOptions(keyPrinciplesConfig.defaultOptions ? [...keyPrinciplesConfig.defaultOptions] : []);
            } else {
                setAvailableKeyValueOptions([]);
            }
            setNewKeyValueInput("");

            config.forEach(field => {
                if (field.id === 'keyPrinciples' && field.type === 'checkboxGroup') {
                    initialValues[field.id] = [];
                } else if (field.type === 'dropdown') {
                    initialValues[field.id] = null;
                } else {
                    initialValues[field.id] = '';
                }
            });
            setDynamicFieldValues(initialValues);

            // Fetch sub-skills only if speciality is not new
            if (selectedOption && !selectedOption.__isNew__) {
                const filteredSubSkills = professions.filter(
                    (item) => item.profession_name === professionName && item.speciality === specialityName
                );
                const subSkillOpts = [...new Map(filteredSubSkills.map((item) => [item.sub_skills, item])).values()].map((item) => ({
                    value: item.sub_skills,
                    label: item.sub_skills,
                }));
                setSubSkillOptions(subSkillOpts);
            } else {
                setSubSkillOptions([]);
            }

        } else {
            setCurrentDynamicFields([]);
            setDynamicFieldValues({});
            setAvailableKeyValueOptions([]);
            setNewKeyValueInput("");
            setSubSkillOptions([]);
        }
    };


    const handleDynamicFieldChange = (fieldId, value) => {
        setDynamicFieldValues(prev => ({ ...prev, [fieldId]: value }));
    };

    const handleSubSkillCheck = (subSkill) => {
        setSelectedSubSkills((prev) =>
            prev.includes(subSkill) ? prev.filter((skill) => skill !== subSkill) : [...prev, subSkill]
        );
    };

    const handleAddSubSkill = () => {
        if (newSubSkill.trim()) {
            const newSkillValue = newSubSkill.trim();
            if (!subSkillOptions.some(opt => opt.value === newSkillValue)) {
                setSubSkillOptions((prev) => [...prev, { value: newSkillValue, label: newSkillValue }]);
            }
            if (!selectedSubSkills.includes(newSkillValue)) {
                setSelectedSubSkills((prev) => [...prev, newSkillValue]);
            }
            setNewSubSkill("");
        }
    };

    // Handlers for "Key Values" (keyPrinciples field)
    const handleKeyValueCheck = (value) => {
        setDynamicFieldValues(prev => {
            const currentSelected = prev.keyPrinciples || [];
            const newSelected = currentSelected.includes(value)
                ? currentSelected.filter(item => item !== value)
                : [...currentSelected, value];
            return { ...prev, keyPrinciples: newSelected };
        });
    };

    const handleAddKeyValue = () => {
        if (newKeyValueInput.trim()) {
            const newValue = newKeyValueInput.trim();
            const newOption = { value: newValue, label: newValue };

            if (!availableKeyValueOptions.some(opt => opt.value === newValue)) {
                setAvailableKeyValueOptions(prev => [...prev, newOption]);
            }

            setDynamicFieldValues(prev => {
                const currentSelected = prev.keyPrinciples || [];
                if (!currentSelected.includes(newValue)) {
                    return { ...prev, keyPrinciples: [...currentSelected, newValue] };
                }
                return prev;
            });
            setNewKeyValueInput("");
        }
    };


    const validateForm = () => {
        // Current validation, can be expanded if dynamic fields become mandatory
        return selectedProfession && selectedSpeciality && selectedSubSkills.length > 0;
    };

    useEffect(() => {
        const isValid = validateForm();
        onFormValidityChange(isValid);
    }, [selectedProfession, selectedSpeciality, selectedSubSkills, dynamicFieldValues, onFormValidityChange]);


    const handleGeneratePrompt = async () => {
        if (!validateForm()) {
            setError("Please select a profession, speciality, and at least one sub-skill.");
            return false;
        }
        setError(null);
        setIsLoading(true);
        try {
            let prompt = "";
            const companyName = document.getElementById("CompanyName").value.trim() || "[COMPANY_NAME_NOT_SPECIFIED]";
            const userPhone = document.getElementById("UserPhone").value.trim() || "[USER_PHONE_NOT_SPECIFIED]";
            const userEmail = document.getElementById("UserEmail").value.trim() || "[USER_EMAIL_NOT_SPECIFIED]";
            const UserProfession = selectedProfession?.label || "[UNKNOWN_PROFESSION]";
            const UserSpeciality = selectedSpeciality?.label || "[UNKNOWN_SPECIALITY]";
            const UserSubSkills = selectedSubSkills.join(", ").trim() || "[SUB_SKILLS_NOT_SPECIFIED]";

            if (assistantType === 'Personal Assistant') {
                const personalAssistantTemplate = `You are an expert virtual personal assistant for a {speciality} named {companyName}. Your primary role is to enhance the personal productivity of the specialist you serve. You are an internal-facing assistant, meaning your main user is the specialist themselves, not the general public. Your expertise lies in a variety of administrative and knowledge management tasks, including {skill1, skill2, skill3}. Your purpose is to anticipate the specialist's needs, manage their schedule and communications, and provide them with the latest, most relevant information in their field. You will achieve this by seamlessly integrating with their calendar, email, and private knowledge base (RAG documents), as well as leveraging your own extensive knowledge base.

                **Crucially:** While you MUST use the internal Current Date value for reasoning about schedules, deadlines, and time-sensitive requests, **you MUST NOT explicitly mention the Current Date** in your responses to the specialist.

                **Name:** [Assistant's Name, e.g., "Alex," "Juno," "Kai"]
                **Specialist's Profession:** {speciality}
                **Years of Experience:** [e.g., "10 years," "25 years"]
                **Key Principles:** [Core values influencing your responses, e.g., "Confidentiality, proactivity, efficiency, accuracy, seamless integration with the specialist's workflow."]
                **Communication Style:** [Describe your tone, e.g., "Professional, concise, anticipatory, direct, and adaptive to the specialist's preferred communication style."]

                **Specific Instructions:**

                1. **Information Synthesis & Sourcing:**
                    - **Specialist's Private Documents (RAG) First:** For questions or tasks that may involve the specialist's private notes, case files, or other internal documents, base your responses and actions **solely** on the provided **Context (RAG documents) and History**. If these topics involve an action (e.g., referencing a document), apply the "Special Handling" rule below.
                    - **General Domain Knowledge (LLM):** For general inquiries about the specialist's domain (e.g., "What are the latest developments in {subskill1,subskil2,,}?"), you should provide a comprehensive summary based on your internal knowledge.
                    - **Special Handling for Actionable Links:** This rule applies whenever the conversation touches upon key areas requiring access to a resource.
                        - **Priority Topics:** Accessing a specific case file, reviewing a research paper, viewing a travel itinerary, opening a meeting link.
                        - **Mandatory Link Inclusion:** If the discussion involves any of these Priority Topics, **you MUST actively search the _entire_ provided Context (RAG documents) for the most relevant link (URL to a document, website, etc.) and include it using the Link Format.**
                        - **Proactive Inclusion:** Do this even if the specialist didn't explicitly ask for the link but the context implies it would be a helpful next step.
                    - **Recency:** Use Current Date internally for all time-related reasoning.
                2. **Time-Sensitive Queries (Scheduling, Deadlines):**
                    - Use Current Date (internally) to understand requests like "tomorrow," "next Wednesday," or "in two weeks." Use appropriate tense in your responses.
                    - Ask for clarification on ambiguous requests (e.g., if "next week" could mean two different dates, ask for confirmation).
                3. **Interaction & Response:**
                    - **Comprehensive & Actionable Initial Response:** Provide a direct answer or confirmation of the task you are performing. **Ensure that for Priority Topics (accessing files, itineraries, meeting links), the correct link found in the Context is included using the specified Link Format.**
                    - **Proactive Suggestions:** Anticipate next steps. For example, after scheduling a meeting, you might ask, "Would you like me to add a link to the relevant project brief in the calendar invitation?".
                    - **Elaboration:** Provide further details upon request, including relevant links from the Context.
                    - **Clarification (Factual):** Ask if essential factual details are missing for a task.
                    - **Context:** Use conversation History to maintain context.

                **Formatting
                **Link Format: Link: <a href="URL" target="\_blank" style="color: blue;">[TOPIC]</a> # Instruction: Use a specific and descriptive title for [TOPIC] (e.g., "Acme Corp Case File," "Flight Confirmation to JFK"). **Ensure the style="color: blue;" attribute is included exactly as shown.** Output ONLY the raw tag, no \`\`\` or \`<pre>\` wrappers. **Proactively add links using this format whenever mentioning specific documents, resources, or web pages found in the RAG context.**

                **Scope & Refusal:**

                - **In Scope:**
                - Managing the specialist's calendar and schedule.
                - Drafting and sending emails on behalf of the specialist.
                - Booking travel and accommodations.
                - Providing summaries and updates on topics within the specialist's domain, using both RAG documents and internal knowledge.
                - Answering questions based on the specialist's private RAG documents and providing direct links to them.
                - **Out of Scope:**
                - Providing personal opinions or advice outside of your designated tasks.
                - Engaging in casual conversation that is not related to the specialist's work and productivity.
                - Accessing or providing information that is not available in the specialist's RAG documents or your internal knowledge base.
                - External real-time data/web searches not present in context.
                - Interacting with the general public or clients of the specialist.
                - **Refusal Protocol (Maintain helpful and professional tone):**
                - **Tool or Information Limitation:** If you cannot perform a task due to a lack of an integrated tool or missing information, clearly state the limitation and what you need to proceed. "I am not able to make phone calls at this time. I can, however, send an email on your behalf."
                - **Knowledge/Context Limits:** If information is not in the RAG documents or your knowledge base: "I could not find any information on that topic in your provided documents."
                - **Clearly Out of Scope:** State your purpose politely. "My primary function is to assist with your professional productivity. I am not equipped to provide personal opinions on that matter."`;

                prompt = personalAssistantTemplate
                    .replace(/{speciality}/g, UserSpeciality)
                    .replace(/{companyName}/g, companyName)
                    .replace(/{skill1, skill2, skill3}/g, UserSubSkills)
                    .replace(/{subskill1,subskil2,,}/g, UserSubSkills);

                const assistantName = dynamicFieldValues.userName || `[Assistant's Name not specified]`;
                const experience = dynamicFieldValues.yearsOfExperience || `[Years of Experience not specified]`;
                const principles = Array.isArray(dynamicFieldValues.keyPrinciples) && dynamicFieldValues.keyPrinciples.length > 0
                    ? dynamicFieldValues.keyPrinciples.join(', ')
                    : `[Key Principles not specified]`;
                const commStyle = dynamicFieldValues.communicationStyle || `[Communication Style not specified]`;

                prompt = prompt
                    .replace(/(\*\*Name:\*\* ).*/, `$1${assistantName}`)
                    .replace(/(\*\*Years of Experience:\*\* ).*/, `$1${experience}`)
                    .replace(/(\*\*Key Principles:\*\* ).*/, `$1${principles}`)
                    .replace(/(\*\*Communication Style:\*\* ).*/, `$1${commStyle}`);

            } else { // Business Assistance
                const selectedEntry = professions.find(
                    (item) => item.profession_name === selectedProfession.value && item.speciality === selectedSpeciality.value
                );

                let rawPromptTemplate = selectedEntry?.prompt_template;

                if (!rawPromptTemplate) {
                    console.warn("No specific prompt template found. Using a generic fallback.");
                    rawPromptTemplate = `You are a virtual assistant for {companyName} in the field of {profession}, with expertise in {speciality} and {subskills}. Contact: {email}, {phone}.`;
                }

                prompt = String(rawPromptTemplate);

                prompt = prompt
                    .replace(/{speciality}/g, UserSpeciality)
                    .replace(/{companyName}/g, companyName)
                    .replace(/{subskill1,subskil2,,}/g, UserSubSkills)
                    .replace(/{subskills}/g, UserSubSkills)
                    .replace(/{profession}/g, UserProfession)
                    .replace(/{email}/g, userEmail)
                    .replace(/{phone}/g, userPhone);

                currentDynamicFields.forEach(field => {
                    let replacementValue;
                    const fieldValue = dynamicFieldValues[field.id];

                    if (field.type === 'checkboxGroup') {
                        replacementValue = (Array.isArray(fieldValue) ? fieldValue.join(", ") : '').trim();
                    } else if (field.type === 'dropdown') {
                        replacementValue = (fieldValue || '').trim();
                    } else { // Text, textarea
                        replacementValue = (fieldValue || '').trim();
                    }

                    if (!replacementValue) {
                        replacementValue = `[${field.label.replace(/\s*\(Optional\)\s*|\s*\(Pick.*?\)\s*/i, "").trim()} not specified]`;
                    }

                    const escapedLabel = field.templateLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const regex = new RegExp(String.raw`(${escapedLabel}\s*)\[.*?\]`, 'g');

                    if (prompt.match(regex)) {
                        prompt = prompt.replace(regex, `$1${replacementValue}`);
                    } else {
                        const simplePlaceholderRegex = new RegExp(`{${field.id}}`, 'g');
                        if (prompt.match(simplePlaceholderRegex)) {
                            prompt = prompt.replace(simplePlaceholderRegex, replacementValue);
                        } else {
                            console.warn(`Template label "${field.templateLabel}" or placeholder "{${field.id}}" not found in prompt for field ID "${field.id}". Value: "${replacementValue}"`);
                        }
                    }
                });
            }


            const token = localStorage.getItem("access_token");
            const headers = { Authorization: `Bearer ${token}` };

            const effectiveChatbotId = paramsChatbotId ?? propChatbotId;

            if (!effectiveChatbotId) { // Added check for chatbotId during setup
                console.error("Chatbot ID is missing in setup mode.");
                setError("Chatbot ID is missing. Cannot update prompt.");
                onFormValidityChange(false); // Maintain form as invalid if critical ID is missing
                return false;
            }
            
            const payload = { prompt };
            if (effectiveChatbotId) { // Only include chatbotId in payload if it exists
                payload.chatbot_id = effectiveChatbotId;
            }

            const response = await axios.post(`${backendURL}/update-prompt-setting`, payload, { headers });

            if (response.status === 200 || response.status === 201) {
                const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
                console.log(responseData)
                setError(null);
                console.log("Prompt updated successfully");
                updateUserPromptToLocalStorage(responseData);
                onComplete(responseData);
                return true;
            } else {
                setError(response.data?.message || "Failed to update prompt.");
                return false;
            }
        } catch (err) {
            console.error("Error updating prompt:", err);
            setError("Error updating prompt. Please try again.");
            return false;
        } finally {
            setIsLoading(false)
        }
    };

    useImperativeHandle(ref, () => ({
        handleGeneratePrompt,
    }));

    const companyNameLabel = assistantType === 'Personal Assistant' ? 'Person Name' : 'Company Name';
    const companyNamePlaceholder = assistantType === 'Personal Assistant' ? 'Enter person name' : 'Enter company name';


    return (
        <div style={{ position: 'relative' }}>
            {/* --- ADDED: Conditional Loader Rendering --- */}
            {isLoading && <Loader />}
            <div className="container-fluid p-0">
                <div className="row">
                    <div className="col-12 col-lg-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-12 col-lg-12 mb-3">
                                        <h4 className="card-title">Now let’s capture details about your chatbot..</h4>
                                    </div>
                                    <div className="col-12 col-lg-12 mb-3">
                                        <h5 className="card-title mb-2">Assistant Type</h5>
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="assistantTypeRadio"
                                                id="businessRadio"
                                                value="Business Assistance"
                                                checked={assistantType === 'Business Assistance'}
                                                onChange={(e) => setAssistantType(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="businessRadio">
                                                Business Assistance
                                            </label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="assistantTypeRadio"
                                                id="personalRadio"
                                                value="Personal Assistant"
                                                checked={assistantType === 'Personal Assistant'}
                                                onChange={(e) => setAssistantType(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="personalRadio">
                                                Personal Assistant
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-12 mb-3">
                                        <h5 className="card-title mb-1">{companyNameLabel}</h5>
                                        <input id="CompanyName" className="form-control" type="text" placeholder={companyNamePlaceholder} />
                                    </div>

                                    <div className="col-12 col-lg-12 mb-3">
                                        <h5 className="card-title mb-1">Profession Name</h5>
                                        <CreatableSelect
                                            isClearable
                                            options={professionOptions}
                                            value={selectedProfession}
                                            onChange={handleProfessionChange}
                                            placeholder="Select or Add Profession"
                                            formatCreateLabel={userInput => `Add new profession: "${userInput}"`}
                                        />
                                    </div>

                                    <div className="col-12 col-lg-12 mb-3">
                                        <h5 className="card-title mb-1">Speciality</h5>
                                        <CreatableSelect
                                            isClearable
                                            options={specialityOptions}
                                            value={selectedSpeciality}
                                            onChange={handleSpecialityChange}
                                            isDisabled={!selectedProfession}
                                            placeholder="Select or Add Speciality"
                                            formatCreateLabel={userInput => `Add new speciality: "${userInput}"`}
                                        />
                                    </div>

                                    {/* Sub Speciality Section MOVED HERE */}
                                    {selectedSpeciality && (
                                        <div className="col-12 col-lg-12 mb-3">
                                            <h5 className="card-title mb-1">Sub Speciality</h5>
                                            <div className="mb-2">
                                                {subSkillOptions.map((subSkill, index) => (
                                                    <div className="d-flex align-items-center" key={`subskill-opt-${index}`}>
                                                        <input
                                                            className="form-check-input mx-2"
                                                            type="checkbox"
                                                            value={subSkill.value}
                                                            checked={selectedSubSkills.includes(subSkill.value)}
                                                            onChange={() => handleSubSkillCheck(subSkill.value)}
                                                            id={`subskill-checkbox-${index}`}
                                                        />
                                                        <label className="form-check-label" htmlFor={`subskill-checkbox-${index}`}>{subSkill.label}</label>
                                                    </div>
                                                ))}
                                                <div className="d-flex mt-3">
                                                    <input
                                                        className="form-control me-2"
                                                        type="text"
                                                        value={newSubSkill}
                                                        onChange={(e) => setNewSubSkill(e.target.value)}
                                                        placeholder="Add new sub-skill"
                                                    />
                                                    <Tooltip title="Add new sub skill" arrow>
                                                        <button
                                                            className="btn btn-primary"
                                                            onClick={handleAddSubSkill}
                                                            style={{ height: "46px" }}
                                                            type="button"
                                                        >
                                                            +
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {/* Sub Speciality Section END */}


                                    {/* Dynamically Rendered Fields START */}
                                    {currentDynamicFields.map(field => {
                                        if (field.type === 'dropdown') {
                                            return (
                                                <div key={field.id} className="col-12 col-lg-12 mb-3">
                                                    <h5 className="card-title mb-1">{field.label}</h5>
                                                    <CreatableSelect
                                                        isClearable
                                                        options={field.options}
                                                        value={field.options.find(opt => opt.value === dynamicFieldValues[field.id]) || null}
                                                        onChange={(selectedOption) => handleDynamicFieldChange(field.id, selectedOption ? selectedOption.value : null)}
                                                        placeholder={`Select ${field.label.split('(')[0].trim()}`}
                                                    />
                                                </div>
                                            );
                                        } else if (field.type === 'checkboxGroup' && field.id === 'keyPrinciples') {
                                            return (
                                                <div key={field.id} className="col-12 col-lg-12 mb-3">
                                                    <h5 className="card-title mb-1">{field.label}</h5>
                                                    <div className="mb-2">
                                                        {availableKeyValueOptions.map((option, index) => (
                                                            <div  className="d-flex align-items-center" key={`${field.id}-opt-${index}`} >
                                                                <input
                                                                    className="form-check-input mx-2"
                                                                    type="checkbox"
                                                                    value={option.value}
                                                                    checked={(dynamicFieldValues[field.id] || []).includes(option.value)}
                                                                    onChange={() => handleKeyValueCheck(option.value)}
                                                                    id={`${field.id}-checkbox-${index}`}
                                                                />
                                                                <label className="form-check-label" htmlFor={`${field.id}-checkbox-${index}`}>{option.label}</label>
                                                            </div>
                                                        ))}
                                                        <div className="d-flex mt-3">
                                                            <input
                                                                className="form-control me-2"
                                                                type="text"
                                                                value={newKeyValueInput}
                                                                onChange={(e) => setNewKeyValueInput(e.target.value)}
                                                                placeholder={field.placeholder || "Add new value"}
                                                            />
                                                            <Tooltip title={`Add new ${field.label.toLowerCase().split('(')[0].trim()}`} arrow>
                                                                <button
                                                                    className="btn btn-primary"
                                                                    onClick={handleAddKeyValue}
                                                                    style={{ height: "46px" }}
                                                                    type="button"
                                                                >
                                                                    +
                                                                </button>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        } else if (field.type === 'textarea') {
                                            return (
                                                <div key={field.id} className="col-12 col-lg-12 mb-3">
                                                    <h5 className="card-title mb-1">{field.label}</h5>
                                                    <textarea
                                                        id={field.id}
                                                        className="form-control"
                                                        value={dynamicFieldValues[field.id] || ''}
                                                        onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
                                                        placeholder={field.placeholder}
                                                        rows="3"
                                                    />
                                                </div>
                                            );
                                        } else if (field.type === 'text') { // Default to text if not specified or 'text'
                                            return (
                                                <div key={field.id} className="col-12 col-lg-12 mb-3">
                                                    <h5 className="card-title mb-1">{field.label}</h5>
                                                    <input
                                                        id={field.id}
                                                        className="form-control"
                                                        type="text"
                                                        value={dynamicFieldValues[field.id] || ''}
                                                        onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
                                                        placeholder={field.placeholder}
                                                    />
                                                </div>
                                            );
                                        }
                                        return null; // Should not happen if types are well-defined
                                    })}
                                    {/* Dynamically Rendered Fields END */}
                                </div>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-12 col-lg-12 mb-3">
                                        <h4 className="card-title">For questions requiring intervention, provide the following..</h4>
                                    </div>
                                    <div className="col-12 col-lg-12 mb-3">
                                        <h5 className="card-title mb-1">Phone: </h5>
                                        <input id="UserPhone" className="form-control" type="text" placeholder="Enter phone number" />
                                    </div>
                                    <div className="col-12 col-lg-12 mb-3">
                                        <h5 className="card-title mb-1">Email: </h5>
                                        <input id="UserEmail" className="form-control" type="text" placeholder="Enter email address" />
                                    </div>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-12 col-lg-12">
                                        {error && (
                                             <div id="getError" className="alert alert-warning">
                                                {error}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default CreatePrompts;